import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeModules } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Box } from '@/components/lib/components';
import { useNonWithdrawableVerificationCheck } from '@/feature/account-details/hooks/use-non-withdrawable-verification-check';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { navigateToAccountSuspendedModal } from '@/feature/authentication/utils';
import {
    LOG_TAG,
    TrustlyKycResponse,
    useTrustlyKyc,
    useTrustlyKycAndDeposit,
} from '@/feature/kyc/hooks/use-trustly-kyc';
import { TrustlyStatus } from '@/feature/kyc/types';
import { useActiveProductName } from '@/hooks/use-active-product-name';
import { failedKycCountSelector, kycVerifiedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { useUserStore } from '@/hooks/use-user-store';
import { DepositStatusNavigationProp } from '@/navigation/deposit/types';
import { navigateBasedOnKYCCount, navigateToKycSelector, popToHome, useCoreNavigation } from '@/navigation/navigation';
import { DepositStackParamList } from '@/navigation/types';
import { read } from '@/utils/async-storage';
import { getWalletErrorCode } from '@/utils/get-wallet-error-code';
import { logger } from '@/utils/logging';
import { getProductRoot } from '@/utils/product';

import { TrustlyStatusMessage } from '../components/TrustlyStatusMessage';
import { PaymentMethodTypes } from '../const';
import { DepositErrorResponse, useDeposit } from '../hooks/use-deposit';
import { debitPaymentSelector, useDepositPaymentMethods } from '../hooks/use-deposit-payment-methods';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { useTrustlyHandleAvailableFundsGuidance } from '../hooks/use-trustly-handle-available-funds-guidance';
import { useTrustlyStatus } from '../hooks/use-trustly-status';
import { PayPalStatus, Paysafe3dsStatus } from '../utils';
import { TrustlyStatus as TrustlyStatusFromStatusCheckApi, TrustlyStatusValue } from '../utils/trustly-status';

const { PaymentsWebView } = NativeModules;

type DepositStatusProps = NativeStackScreenProps<DepositStackParamList, 'DepositStatusModal'>;

export const DepositStatus = ({ route }: DepositStatusProps) => {
    const { paymentProvider, currentStatus } = route.params;
    const hasSocialWallet = useHasSocialWallet();
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackage = useDepositStatusStore(state => state.coinPackage);
    const navigation = useNavigation<DepositStatusNavigationProp>();

    usePreventAndroidBackNavigation();

    const productName = useActiveProductName();
    const selectedProduct = useJurisdictionStore.getState().product;
    const root = getProductRoot(selectedProduct);
    const { t } = useTranslation(['kyc', 'common', 'wallet']);
    const tSocialWallet = useSocialWalletTranslation(['kyc', 'wallet']);
    const isTrustlyProcessingRef = useRef(false);
    const actions = useUserStore(state => state.actions);
    const { openLaunch } = useCoreNavigation();

    const { data: retryCount = 0 } = useAuthUserConfig({
        select: failedKycCountSelector,
        refetchOnMount: 'always',
    });
    const { data: isKycVerified } = useAuthUserConfig({ select: kycVerifiedSelector });
    const { data: debitMethod } = useDepositPaymentMethods({
        select: debitPaymentSelector,
    });

    const rootNavigation = useRootNavigation();

    const handleAvailableFundsGuidance = useTrustlyHandleAvailableFundsGuidance();

    // Navigation handlers
    const navigateToDebitDeposit = useCallback(() => {
        if (debitMethod) {
            navigation.replace('AddDebitCardWebView', {
                paysafeAccountId: debitMethod.accountId ?? '',
                closeIconHandlePress: () => {
                    popToHome(rootNavigation, 'lobby');
                },
                canUse3ds: debitMethod?.type === PaymentMethodTypes.Paysafe3ds,
            });
        } else {
            logger.error(LOG_TAG, 'No debit method available for navigation');
        }
    }, [debitMethod, navigation, rootNavigation]);

    const handleNavigateToIdcomply = useCallback(() => {
        navigateBasedOnKYCCount(rootNavigation, retryCount);
    }, [rootNavigation, retryCount]);

    // Trustly KYC error and success handlers
    const handleTrustlyVerificationError = useCallback(async () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_FAILED);
        navigation.navigate('ErrorModal', {
            title: t('trustly_verification_failed_title'),
            subtitle: t('trustly_verification_failed_description'),
            primaryButton: t('manually_verify'),
            handlePress: handleNavigateToIdcomply,
            showNavbarTitle: false,
            showSecondaryButton: false,
            closeIconHandlePress: () => {
                actions.logout();
                openLaunch();
            },
        });
    }, [navigation, t, handleNavigateToIdcomply, actions, openLaunch]);

    const handleTrustlyDepositError = useCallback(
        async (trustlyStatus?: TrustlyStatusFromStatusCheckApi) => {
            BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_FAILED);

            let additionalInfo;
            if (
                trustlyStatus === TrustlyStatusValue.INSUFFICIENT_FUNDS ||
                trustlyStatus === TrustlyStatusValue.FRAUD_ANALYSIS ||
                trustlyStatus === TrustlyStatusValue.INVALID_ACCOUNT
            ) {
                additionalInfo = <TrustlyStatusMessage status={trustlyStatus} />;
            }

            if (isKycVerified) {
                return navigation.navigate('FailedDepositModal', {
                    paymentProvider: PaymentMethodTypes.Trustly,
                    errorCode: `trustly_status_${trustlyStatus}`,
                });
            }

            navigation.navigate('SuccessModal', {
                title: t('trustly_verification_successful_title'),
                subtitle: tSocialWallet('trustly_deposit_failed_description'),
                additionalInfo,
                primaryButton: tSocialWallet('wallet:deposit_amount_with_debit', { amount: selectedAmount }),
                secondaryButton: t('common:go_to_product_lobby', { product: productName }),
                handlePress: navigateToDebitDeposit,
                handleSecondaryPress: () => {
                    popToHome(rootNavigation, 'lobby');
                },
                closeIconHandlePress: () => {
                    actions.logout();
                    openLaunch();
                },
            });
        },
        [
            isKycVerified,
            navigation,
            rootNavigation,
            t,
            tSocialWallet,
            selectedAmount,
            productName,
            navigateToDebitDeposit,
            actions,
            openLaunch,
        ]
    );

    const handleTrustlyVerificationAndDepositSuccess = useCallback(async () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_SUCCESS);
        navigation.navigate('SuccessfulDepositModal', {
            paymentProvider,
            title: isKycVerified
                ? hasSocialWallet
                    ? t('coin_purchase_successful')
                    : t('trustly_subsequent_deposit_success_title')
                : tSocialWallet('trustly_success_title'),
            shouldRedirectToLobby: true,
            disableTransition: isKycVerified,
            description: isKycVerified
                ? hasSocialWallet
                    ? t('coin_purchase_successful_description', {
                          betr_gold: coinPackage?.coin_amount,
                          betr_cash: coinPackage?.cash_amount,
                      })
                    : t('trustly_subsequent_deposit_success_description', {
                          amount: selectedAmount,
                          product: productName,
                      })
                : t('trustly_success_description', {
                      amount: selectedAmount,
                      product: productName,
                  }),
        });
    }, [
        isKycVerified,
        navigation,
        t,
        tSocialWallet,
        paymentProvider,
        selectedAmount,
        productName,
        hasSocialWallet,
        coinPackage,
    ]);

    // Trustly status hook
    const { startTimerForCheckStatusApiAsFallback } = useTrustlyStatus({
        onSuccess: (status: TrustlyStatusFromStatusCheckApi) =>
            status === TrustlyStatusValue.SUCCESS
                ? handleTrustlyVerificationAndDepositSuccess()
                : handleTrustlyDepositError(status),
        onError: () => handleTrustlyDepositError(),
        onAvailableFundsGuidance: handleAvailableFundsGuidance,
        isWebsocketActive: !!isTrustlyProcessingRef.current,
    });

    const { shouldPerformTrustlyVerificationCheck } = useNonWithdrawableVerificationCheck();
    const { mutate: triggerTrustlyKycAndDeposit } = useTrustlyKycAndDeposit();
    const { mutate: triggerTrustlyKycOnly } = useTrustlyKyc();
    const { mutate: deposit } = useDeposit();
    const handleTrustlyTransaction = useCallback(
        async (isRefresh: boolean = false) => {
            const transactionId = (await read('trustlyTransactionId')) ?? '';

            if (!transactionId) {
                navigation.navigate('FailedDepositModal', {
                    paymentProvider: PaymentMethodTypes.Trustly,
                    errorCode: 'missing_transaction_id', // Passing errorCode here for trackability
                });
                return;
            }

            if (shouldPerformTrustlyVerificationCheck) {
                triggerTrustlyKycOnly(
                    {
                        transactionId,
                    },
                    {
                        onSuccess: (responseData: TrustlyKycResponse) => {
                            if (!responseData.verification_success) {
                                return handleTrustlyVerificationError();
                            }
                            if (responseData.is_user_blocked) {
                                return navigateToAccountSuspendedModal(rootNavigation);
                            }
                            if (responseData.deposit_status !== 'success') {
                                return handleTrustlyDepositError();
                            }
                            if (
                                responseData.verification_success &&
                                responseData.deposit_status === 'success' &&
                                responseData.user_wallet_id
                            ) {
                                return startTimerForCheckStatusApiAsFallback(responseData.user_wallet_id);
                            }
                        },
                        onError: handleTrustlyVerificationError,
                    }
                );
                return;
            }

            if (isKycVerified) {
                // Directly deposit if KYC is verified
                deposit(
                    {
                        type: PaymentMethodTypes.Trustly,
                        currency: 'USD',
                        transaction_id: transactionId,
                        ...(isRefresh && { payment_type_id: paymentProvider }),
                    },
                    {
                        onSuccess: data => {
                            if (data.status !== 'success') {
                                return navigation.navigate('FailedDepositModal', {
                                    paymentProvider: PaymentMethodTypes.Trustly,
                                });
                            }
                            return startTimerForCheckStatusApiAsFallback(data?.extra.user_wallet_id);
                        },
                        onError: (error: DepositErrorResponse) => {
                            navigation.navigate('FailedDepositModal', {
                                paymentProvider: PaymentMethodTypes.Trustly,
                                errorCode: getWalletErrorCode(error),
                            });
                        },
                    }
                );
                return;
            }

            // Handle KYC and deposit for unverified users
            triggerTrustlyKycAndDeposit(
                {
                    transactionId,
                    amount: selectedAmount,
                },
                {
                    onSuccess: (responseData: TrustlyKycResponse) => {
                        if (!responseData.verification_success) {
                            return handleTrustlyVerificationError();
                        }
                        if (responseData.is_user_blocked) {
                            return navigateToAccountSuspendedModal(rootNavigation);
                        }
                        if (responseData.deposit_status !== 'success') {
                            return handleTrustlyDepositError();
                        }
                        if (
                            responseData.verification_success &&
                            responseData.deposit_status === 'success' &&
                            responseData.user_wallet_id
                        ) {
                            return startTimerForCheckStatusApiAsFallback(responseData.user_wallet_id);
                        }
                    },
                    onError: handleTrustlyVerificationError,
                }
            );
        },
        [
            triggerTrustlyKycAndDeposit,
            triggerTrustlyKycOnly,
            selectedAmount,
            navigation,
            rootNavigation,
            deposit,
            handleTrustlyVerificationError,
            handleTrustlyDepositError,
            startTimerForCheckStatusApiAsFallback,
            isKycVerified,
            paymentProvider,
            shouldPerformTrustlyVerificationCheck,
        ]
    );

    // Handle processing when return from the webview
    useEffect(() => {
        const processDepositLink = async () => {
            try {
                PaymentsWebView?.closeWebView();
            } catch (err) {
                console.warn('Unable to close webview: ', err);
            }

            // Handle different status cases
            switch (true) {
                case currentStatus === PayPalStatus.SUCCESS:
                    navigation.navigate('SuccessfulDepositModal', {
                        paymentProvider,
                    });
                    break;

                case currentStatus === PayPalStatus.ERROR:
                    navigation.navigate('FailedDepositModal', {
                        paymentProvider,
                    });
                    break;

                case Boolean(currentStatus?.includes(TrustlyStatus.TRANSACTION)):
                    if (!isTrustlyProcessingRef.current) {
                        isTrustlyProcessingRef.current = true;
                        await handleTrustlyTransaction();
                    }
                    break;

                case Boolean(currentStatus?.includes(TrustlyStatus.REFRESH)):
                    if (!isTrustlyProcessingRef.current) {
                        isTrustlyProcessingRef.current = true;
                        await handleTrustlyTransaction(true);
                    }

                    break;

                case Boolean(currentStatus?.includes(TrustlyStatus.REFRESH_CANCEL)):
                    navigation.navigate('FailedDepositModal', {
                        paymentProvider: PaymentMethodTypes.Trustly,
                    });
                    break;

                case Boolean(currentStatus?.includes(TrustlyStatus.CANCEL)):
                    navigateToKycSelector(rootNavigation);
                    break;

                case Boolean(currentStatus?.includes(Paysafe3dsStatus.SUCCESS)):
                    navigation.navigate('SuccessfulDepositModal', {
                        paymentProvider: PaymentMethodTypes.Paysafe3ds,
                    });
                    break;

                case Boolean(currentStatus?.includes(Paysafe3dsStatus.ERROR)):
                    navigation.navigate('FailedDepositModal', {
                        paymentProvider: PaymentMethodTypes.Paysafe3ds,
                    });
                    break;

                default:
                    isTrustlyProcessingRef.current = false;
                    rootNavigation.reset({
                        routes: [
                            {
                                name: root,
                                params: { screen: 'AccountStack' },
                            },
                            {
                                name: 'DepositStack',
                                params: { screen: hasSocialWallet ? 'CoinPackageScreen' : 'DepositScreen', params: {} },
                            },
                        ],
                    });
                    break;
            }
        };

        processDepositLink();
    }, [
        currentStatus,
        navigation,
        rootNavigation,
        paymentProvider,
        selectedAmount,
        root,
        handleTrustlyTransaction,
        isKycVerified,
        hasSocialWallet,
    ]);

    return (
        <Box bg="neutral900" flex={1} justifyContent="center">
            <LoadingSpinner />
        </Box>
    );
};
